<template>
  <div
    class="rf-live flex h-full items-end"
    :class="fullView ? 'flex-col' : '5xl:flex-row flex-col'"
  >
    <RfTooltip ref="tooltipEl" :anchor="tooltipAnchorEl?.$el">
      <template #tooltip>
        <div class="w-64">Only the first 500 results matching your search will be exported</div>
      </template>
    </RfTooltip>
    <div class="flex self-start">
      <slot name="title" />
      <div v-if="!fullView" class="absolute top-0 right-0 z-10 inline-flex items-center gap-4 p-4">
        <RfButtonInput
          ref="searchInputRef"
          v-model="searchInput"
          text="Search"
          placeholder="Search by User ID"
          :icon="RfSearchIcon"
        />
        <Transition mode="out-in" name="fade">
          <RfSwitch v-if="searchInputRef.closed" v-model="liveAutoUpdate" title="Auto update" />
        </Transition>
        <span class="inline-flex items-center gap-2">
          <RfButtonNew :disabled="skeleton" size="large" type="icon" @click="fetchMetrics">
            <RfUpdateIcon class="fill-grey-1! h-6! w-6!" />
          </RfButtonNew>
          <RfButtonNew
            ref="tooltipAnchorEl"
            :disabled="loadingCsv"
            type="icon"
            size="large"
            @click="exportLiveDataToCsv"
            @focus="() => tooltipEl?.show?.()"
            @mouseenter="() => tooltipEl?.show?.()"
            @blur="() => tooltipEl?.hide?.()"
            @mouseleave="() => tooltipEl?.hide?.()"
          >
            <RfDownloadIcon class="fill-grey-1! h-6! w-6!" />
          </RfButtonNew>
        </span>
      </div>
    </div>
    <div class="w-full" :class="{ '5xl:max-w-2/3': !fullView }">
      <div v-if="fullView" class="flex flex-wrap items-center justify-end gap-x-4 gap-y-3">
        <RfDrillDownMetricsFilters
          v-model:filtersData="filtersData"
          class="mr-auto ml-0 place-self-start self-start justify-self-start"
          :segments="allSegmentsStore.sortedAllSegments"
          :segmentFilterIsLoading="allSegmentsStore.getSegmentsLoading"
          :appDevices="currApp?.path_filter?.dimension_filters?.device_categories"
          :appLanguages="currApp?.path_filter?.dimension_filters?.languages"
          :appDeviceTypes="appDeviceTypeEntries"
        />
        <span class="flex items-center gap-x-4 overflow-auto">
          <RfFilter
            variant="outlined"
            activator="Prompt type"
            class="z-10! shrink-0"
            :selected="filters"
            :groups="{
              prompts: { el: getAllPromptTypes(), key: 'key', value: 'value' },
            }"
            @selected="items => (filters = { ...filters, ...items })"
          />
          <RfFilter
            variant="outlined"
            activator="Activity type"
            class="z-10! shrink-0"
            :selected="filters"
            :groups="{ activities: { el: allActivities, key: 'key', value: 'value' } }"
            @selected="items => (filters = { ...filters, ...items })"
          />
          <RfFilter
            v-if="fullView"
            variant="outlined"
            activator="Prompts"
            class="z-10!"
            search="Search by prompt name"
            :selected="filters"
            :groups="{ paths: { el: allPromptsStore.sortedAllPrompts, key: 'id', value: 'name' } }"
            :isLoading="allPromptsStore.getPromptsLoading"
            @selected="items => (filters = { ...filters, ...items })"
            @show="
              () =>
                !allPromptsStore.prompts.length &&
                allPromptsStore.getPrompts(currApp.id, { nonBlocking: true })
            "
          />
          <div class="flex-1">
            <RfInput
              v-model="searchInput"
              variant="outlined"
              placeholder="Search by User ID"
              class="flex! max-w-77.5 min-w-52 rounded"
              autoWidth
            >
              <template #prepend>
                <RfSearchIcon class="fill-beige-50! h-6! w-6!" />
              </template>
            </RfInput>
          </div>
        </span>
        <div class="align-center flex flex-none justify-end gap-5">
          <RfSwitch v-model="liveAutoUpdate" title="Auto update" />
          <RfButtonNew
            :disabled="skeleton"
            class="min-w-40 px-0"
            @click="(fetchMetrics(), startAutoUpdate())"
          >
            <span v-if="skeleton" class="flex items-center justify-center gap-4">
              <RfSpinnerIcon class="h-6! w-6! animate-spin" />
              Updating
            </span>
            <span v-else class="flex items-center justify-center gap-4">
              <RfResetIcon class="h-5! w-5!" />
              Update
              {{ tick ? `in ${MAX_TICKS - tick}` : "" }}
            </span>
          </RfButtonNew>
        </div>
        <RfDatePicker
          v-if="fullView"
          ref="datePicker"
          class="[&_.rf-menu--button]:px-6"
          :min="dayjs().subtract(1, 'year')"
          :max="dayjs()"
          @input="v => (datepickerDate = v)"
        />
      </div>
      <div v-if="fullView" class="mb-3 flex items-center">
        <RfTagsGroup
          :filters="filters"
          :filtersLength="filtersLength"
          class="mt-4"
          @remove="removeFilter"
          @clear="filters = {}"
        />
      </div>
      <div
        class="bg-white-1 flex flex-col gap-3 rounded-sm"
        :class="{ 'border-beige-10 border border-solid px-5 py-5': fullView }"
      >
        <Transition v-if="fullView" mode="out-in">
          <RfBaseSkeleton v-if="skeletonOnce" height="29" width="120" srLabel="Loading activity" />
          <h2 v-else class="text-heading-1 text-beige-90">Activity</h2>
        </Transition>

        <div class="flex flex-col">
          <div class="text-action-buttons text-black-2 mx-auto inline-flex h-6 gap-1">
            <Transition mode="out-in" tag="span">
              <span v-if="!skeleton && chart.hasData && dataValues?.total">
                <span class="font-bold">{{ toLocaleNumberString(dataValues?.total) }}</span>
                events
              </span>
            </Transition>
          </div>
          <RfChart ref="chart" :skeleton="skeleton" :data="metricsFormatted" noLegend />
        </div>
      </div>
    </div>
    <div
      v-if="!fullView"
      class="bg-strokes 5xl:block mx-6 box-border hidden w-px self-stretch"
    ></div>
    <div
      :class="[
        fullView
          ? '[&_.rf-table--upper-wrapper]:border-beige-10 [&_.rf-table--pretable-wrapper]:rounded-t [&_.rf-table--upper-wrapper]:rounded-t [&_.rf-table--upper-wrapper]:border [&_.rf-table--upper-wrapper]:border-solid [&_.rf-table--wrapper]:pb-5'
          : '5xl:max-w-1/3 min-h-px [&_.rf-table--upper-wrapper]:max-h-full [&_.rf-table--upper-wrapper]:overflow-y-auto',
      ]"
      class="h-full w-full"
    >
      <RfTable
        :tableData="table"
        class="rf-table [&_.rf-table--upper-wrapper]:bg-white-1 [&_.rf-table--upper-wrapper]:rounded-b [&_.rf-table--wrapper__table]:table-fixed"
        :class="{ 'rf-table--full-view': fullView }"
        :hideTotal="!fullView"
        :rowSkeleton="RfTableRowSkeleton"
        :rowSkeletonProps="{ alternative: tabs[tab].key === 'live-errors', fullView }"
        :loading="skeleton && !chart.hasData"
        @loadMore="() => initTable(true)"
      >
        <template #pretable>
          <div class="bg-white-1 flex grow flex-col gap-3 pb-3" :class="{ 'px-5 pt-5': fullView }">
            <div v-if="fullView" class="flex h-10 items-center justify-between gap-2">
              <Transition mode="out-in">
                <RfBaseSkeleton
                  v-if="skeletonOnce"
                  height="29"
                  width="120"
                  srLabel="Loading live"
                />
                <h2 v-else class="text-heading-1 text-beige-90">Live</h2>
              </Transition>
              <Transition mode="out-in">
                <RfBaseSkeleton v-if="skeletonOnce" height="40" width="250" />
                <span v-else-if="renderLength" class="inline-flex items-center gap-3">
                  <RfTooltip class="mr-0 w-fit">
                    <template #activator>
                      <RfHelpIcon
                        class="border-input-background h-7! w-7! rounded-full border border-solid"
                      />
                    </template>
                    <template #tooltip>
                      <div class="w-64">
                        Only the first 500 results matching your search will be exported
                      </div>
                    </template>
                  </RfTooltip>
                  <RfButtonNew
                    ref="tooltipAnchorEl"
                    size="large"
                    :disabled="loadingCsv"
                    @click="exportLiveDataToCsv"
                    @focus="() => tooltipEl?.show?.()"
                    @mouseenter="() => tooltipEl?.show?.()"
                    @blur="() => tooltipEl?.hide?.()"
                    @mouseleave="() => tooltipEl?.hide?.()"
                  >
                    <RfDownloadIcon class="h-6! w-6!" />
                    Export to CSV
                  </RfButtonNew>
                </span>
              </Transition>
            </div>
            <RfButtonTabs :active="tab">
              <RfButtonNew
                v-for="({ name, key }, i) in tabs"
                :key="key"
                type="plain"
                size="large"
                class="[&_.rf-button--container]:text-beige-50 px-4 py-2"
                :class="{
                  '[&_.rf-button--container]:text-black-2 [&_.rf-button--container]:font-bold!':
                    i === tab,
                }"
                @click="tab = i"
              >
                {{ name }}
              </RfButtonNew>
            </RfButtonTabs>
          </div>
        </template>
        <template #thead>
          <RfTableHeader
            :fullView="fullView"
            :errorTab="tabs[tab].key === 'live-errors'"
            :sort="sort"
            :filters="filters"
            :allActivities="allActivities"
            :survey="surveyEnabled"
            @update:sort="v => (sort = v)"
            @update:filters="f => (filters = { ...filters, ...f })"
          />
        </template>
        <template #tbody>
          <template v-if="renderLength">
            <RfTableRow
              v-for="i in renderLength"
              v-bind="data[tabs[tab].key]?.[date.preset?.value]?.hits[i - 1]"
              :key="data[tabs[tab].key]?.[date.preset?.value]?.hits[i - 1]?.document_id || i"
              :errorTab="tabs[tab].key === 'live-errors'"
              :fullView="fullView"
              :activity="
                allActivities?.[data[tabs[tab].key]?.[date.preset?.value]?.hits?.[i - 1]?.activity]
                  ?.value
              "
              :appId="currApp.id"
              :survey="surveyEnabled"
            />
          </template>
          <tr v-if="table.showing >= 500" key="max">
            <td colspan="100%">
              <div class="text-body text-grey-1 w-full text-center">
                These are the first 500 results matching your search, refine your search to see
                others
              </div>
            </td>
          </tr>
        </template>
      </RfTable>
    </div>
  </div>
</template>

<script>
import RfInput from "@/components/inputs/RfInput.vue";
import { computed, nextTick, onUnmounted, ref, watch } from "vue";
import { getAllDevices, getAllPromptTypes } from "@/utils/getDeviceName";
import RfFilter from "@/components/inputs/RfFilter.vue";
import RfTableRow from "@/blocks/RfLive/RfTableRow.vue";
import RfTableHeader from "@/blocks/RfLive/RfTableHeader.vue";
import RfTableRowSkeleton from "@/blocks/RfLive/RfTableRowSkeleton.vue";
import { useTableFilter } from "@/utils/composables/useTableFilter";
import { getAllActivities } from "@/utils/constants/PromoInteractionConstants";
import dayjs from "dayjs";
import RfDatePicker from "@/components/inputs/RfDatePicker.vue";
import { METRICS_PERIODS } from "@/utils/constants/MetricsConstants";
import RfButtonTabs from "@/components/buttons/RfButtonTabs.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import ApiApps from "@/apis/ApiApps";
import ApiPaths from "@/apis/ApiPaths";
import { useRoute } from "vue-router";
import { debounce } from "@/utils/debounce";
import { useTableData } from "@/pinia/piniaUtils";
import RfTable from "@/blocks/RfTable/RfTable.vue";
import { useTableSort } from "@/utils/composables/useTableSort";
import RfResetIcon from "@/components/icons/RfResetIcon.vue";
import RfSpinnerIcon from "@/components/icons/RfSpinnerIcon.vue";
import MetricsUtils from "@/utils/MetricsUtils";
import { useUserSettings } from "@/utils/composables/useUserSettings";
import { chartColors } from "@/utils/constants/ColorsConstants";
import { toLocaleNumberString } from "@/utils/stringHelpers";
import { getAllActivitiesForPrompt, isSurvey } from "@/utils/prompts/promptHelpers";
import RfChart from "@/blocks/RfCharts/RfChart.vue";
import RfBaseSkeleton from "@/components/skeletons/RfBaseSkeleton.vue";
import RfDownloadIcon from "@/components/icons/RfDownloadIcon.vue";
import { exportLiveDataToCsv } from "@/utils/CsvUtils";
import RfTooltip from "@/components/tooltip/RfTooltip.vue";
import RfHelpIcon from "@/components/icons/RfHelpIcon.vue";
import RfSwitch from "@/components/inputs/RfSwitch.vue";
import RfSearchIcon from "@/components/icons/RfSearchIcon.vue";
import RfButtonInput from "@/components/inputs/RfButtonInput.vue";
import RfUpdateIcon from "@/components/icons/RfUpdateIcon.vue";
import { useAppsStore } from "@/pinia/appsStore";
import { useAllPromptsStore } from "@/pinia/allPromptsStore";
import RfTagsGroup from "../RfTagsGroup.vue";
import RfDrillDownMetricsFilters from "../RfDrillDownMetricsFilters.vue";
import { useAllSegmentsStore } from "@/pinia/allSegmentsStore";
import { useFilterData } from "@/utils/composables/useFilterData";

const MAX_TICKS = 30;

const LIVE_UPDATE_PERIODS = new Set([
  METRICS_PERIODS.this_month.value,
  METRICS_PERIODS.this_week.value,
  METRICS_PERIODS.today.value,
  METRICS_PERIODS.last_seven_days.value,
  METRICS_PERIODS.since_start_date.value,
]);

export default {
  name: "RfLiveCard",
  components: {
    RfInput,
    RfTableRow,
    RfTableHeader,
    RfDatePicker,
    RfButtonTabs,
    RfButtonNew,
    RfTable,
    RfResetIcon,
    RfSpinnerIcon,
    RfChart,
    RfBaseSkeleton,
    RfDownloadIcon,
    RfTooltip,
    RfHelpIcon,
    RfSwitch,
    RfButtonInput,
    RfUpdateIcon,
    RfTagsGroup,
    RfSearchIcon,
    RfFilter,
    RfDrillDownMetricsFilters,
  },
  props: {
    dateFilter: Object,
    promptFilter: Object,
    getFilters: Function,
    promptFilters: Object,
    active: { type: Boolean, default: true },
  },
  setup(props) {
    const { getUserSetting, setUserSetting } = useUserSettings();
    const colors = ref([...chartColors]);
    const tabs = [
      {
        name: "Activities",
        key: "live-activities",
        color: colors.value.splice(~~(Math.random() * colors.value.length), 1)[0],
      },
      {
        name: "Errors",
        key: "live-errors",
        color: colors.value.splice(~~(Math.random() * colors.value.length), 1)[0],
      },
    ];
    const route = useRoute();
    const data = ref({});
    const tooltipEl = ref(null);
    const tooltipAnchorEl = ref(null);
    const liveAutoUpdate = ref(null);
    const tick = ref(0);
    let timerId = null;
    const needsRefresh = ref(false);
    const searchInput = ref("");
    const searchInputRef = ref({ closed: true });
    const skeleton = ref(true);
    const skeletonOnce = ref(true);
    const chart = ref({ hasData: false });
    const tab = ref(0);
    const loadingCsv = ref(false);
    const datepickerDate = ref({});
    const debugData = ref(false);
    const { filters, removeFilter, filtersLength, filtersConverted } = useTableFilter();
    const { table, resetPage } = useTableData();
    const sort = useTableSort(["default"]);
    const appsStore = useAppsStore();
    const allPromptsStore = useAllPromptsStore();
    const allSegmentsStore = useAllSegmentsStore();
    const { filtersData, filterDataKeys, getDimensionFilters } = useFilterData();

    const currApp = computed(() => appsStore.app);
    const getAppDevices = computed(() =>
      getAllDevices(currApp.value.custom_devices, currApp.value.flags.custom_devices),
    );
    // Sourced from app config (custom_devices), not path_filter.dimension_filters — device types
    // are known upfront from configuration, unlike categories/languages which require observed data.
    const appDeviceTypeEntries = computed(() =>
      currApp.value.flags?.device_type_analytics
        ? getAppDevices.value.map(d => ({ key: d.value, value: d.text }))
        : [],
    );

    const date = computed(() =>
      datepickerDate.value?.preset?.value ? datepickerDate.value : (props.dateFilter ?? {}),
    );
    const fullView = computed(() => !props.promptFilter);

    const dataValues = computed(
      () => data.value?.[tabs[tab.value].key]?.[date.value?.preset?.value],
    );
    const renderLength = computed(() => {
      const dataLength = dataValues.value?.hits?.length || 0;
      return Math.min(dataLength, (table.page || 1) * table.perPage);
    });

    const initTable = (next = false) => {
      if (next) return (table.page += 1);
      table.page = 1;
      table.totalCount = data.value[tabs[tab.value].key][date.value.preset.value].hits.length;
      table.totalPages = table.totalCount / table.perPage;
    };

    const requestMetrics = debounce(async () => {
      try {
        // Runs at debounce-execution time: a request scheduled while active may fire
        // after the tab was hidden — block it and mark the data stale so the
        // reactivation watch refetches. Watchers must keep scheduling fetchMetrics
        // while hidden; this guard is what turns those schedules into needsRefresh.
        if (!props.active) {
          needsRefresh.value = true;
          return;
        }
        resetPage();
        table.perPage = 50;
        table.totalCount = 50;
        if (debugData.value) date.value.preset = METRICS_PERIODS.custom;
        const device_types = filtersConverted.value.devices?.array;
        const path_types = filtersConverted.value.prompts?.array;
        const activities = filtersConverted.value.activities?.array;
        const paths = filtersConverted.value.paths?.array;

        const dimensionFilters = fullView.value ? getDimensionFilters() : props.getFilters();
        const filtersParam = {
          ...(device_types && { device_types }),
          ...(path_types && { path_types }),
          ...(activities && { activities }),
          ...(paths && { paths }),
        };

        const params = {
          metric_key: tabs[tab.value].key,
          metric_periods: [
            {
              period: date.value.preset.value || METRICS_PERIODS.last_week.value,
              ...(date.value.preset.value === METRICS_PERIODS.custom.value && {
                date_range: [
                  date.value.startDate.format("YYYY-MM-DD"),
                  date.value.endDate.format("YYYY-MM-DD"),
                ],
              }),
            },
          ],
          ...(Object.keys(filtersParam).length && { filters: filtersParam }),
          ...dimensionFilters,
          ...(searchInput.value && { search: { user_id: searchInput.value } }),
          ...(debugData.value && { dummy_response: true }),
        };

        const v = await (props.promptFilter
          ? ApiPaths.getLiveData(route.params.aid, props.promptFilter.id, params)
          : ApiApps.getLiveData(route.params.aid, params));

        data.value = {
          ...data.value,
          [tabs[tab.value].key]: {
            ...data.value[tabs[tab.value].key],
            [date.value.preset.value]: v[date.value.preset.value].agg_data,
          },
        };
        initTable();
      } catch (_e) {
        // empty
      } finally {
        skeleton.value = false;
        skeletonOnce.value = false;
      }
    }, 330);

    const fetchMetrics = () => {
      skeleton.value = true;
      return requestMetrics();
    };

    const destroyInterval = () => {
      clearInterval(timerId);
      timerId = null;
      tick.value = 0;
    };

    const autoUpdate = () => {
      if (tick.value < MAX_TICKS) {
        tick.value++;
        return;
      }
      tick.value = 0;
      fetchMetrics();
    };

    const shouldAutoUpdate = () => {
      if (!date.value.preset?.value) return false;
      const period = date.value.preset.value;
      return (
        LIVE_UPDATE_PERIODS.has(period) ||
        (period === METRICS_PERIODS.custom.value &&
          (date.value.startDate.isToday() || date.value.endDate.isToday()))
      );
    };

    const startAutoUpdate = () => {
      if (!props.active) {
        destroyInterval();
        return;
      }
      if (!date.value.preset?.value) return;
      const restart = shouldAutoUpdate() && liveAutoUpdate.value;
      destroyInterval();
      if (restart) {
        timerId = setInterval(autoUpdate, 1000);
        tick.value++;
      }
    };

    onUnmounted(destroyInterval);

    watch([searchInput, tab, date, filters, sort, filtersData], () => fetchMetrics(), {
      deep: true,
    });

    watch(
      () => props.active,
      active => {
        if (!active) {
          destroyInterval();
          return;
        }
        if (needsRefresh.value || (liveAutoUpdate.value && shouldAutoUpdate())) fetchMetrics();
        needsRefresh.value = false;
        startAutoUpdate();
      },
    );

    const metricsFormatted = computed(() => {
      const dataLength = dataValues.value?.aggregations?.length || 0;
      if (dataLength === 0) return [];
      const chartData = [];
      chartData.push({
        label: "Events",
        data: Object.fromEntries(dataValues.value.aggregations.map(el => [el.date, el.count])),
        backgroundColor: tabs[tab.value].color,
      });
      return chartData;
    });

    const allActivities = computed(() =>
      props.promptFilter ? getAllActivitiesForPrompt(props.promptFilter) : getAllActivities(),
    );

    const surveyEnabled = computed(() => isSurvey(props.promptFilter));

    return {
      tabs,
      tab,
      data,
      tick,
      MAX_TICKS,
      searchInput,
      skeleton,
      skeletonOnce,
      date,
      datepickerDate,
      filters,
      removeFilter,
      filtersLength,
      fullView,
      table,
      sort,
      currApp,
      getAppDevices,
      dataValues,
      renderLength,
      fetchMetrics,
      initTable,
      startAutoUpdate,
      metricsFormatted,
      RfTableRowSkeleton,
      getAllDevices,
      getAllPromptTypes,
      dayjs,
      datePicker: ref(),
      allActivities,
      toLocaleNumberString,
      chart,
      loadingCsv,
      destroyInterval,
      liveAutoUpdate,
      surveyEnabled,
      RfSearchIcon,
      tooltipEl,
      tooltipAnchorEl,
      searchInputRef,
      allPromptsStore,
      allSegmentsStore,
      filtersData,
      filterDataKeys: fullView.value ? filterDataKeys : props?.promptFilters,
      getUserSetting,
      setUserSetting,
      appDeviceTypeEntries,
    };
  },
  watch: {
    date: {
      handler(v) {
        this.startAutoUpdate();
        if (v?.preset?.value && v.preset.value !== METRICS_PERIODS.custom.value && this.fullView)
          this.setUserSetting("liveDateRange", v.preset.value);
      },
      deep: true,
    },
    liveAutoUpdate(v) {
      this.startAutoUpdate();
      if (this.fullView && this.getUserSetting("liveAutoUpdate") !== v)
        this.setUserSetting("liveAutoUpdate", v);
    },
  },
  async mounted() {
    await nextTick();

    if (this.fullView) {
      const preset = MetricsUtils.All_Periods.find(
        ({ value }) =>
          value !== METRICS_PERIODS.since_start_date.value &&
          value === this.getUserSetting("liveDateRange"),
      )?.value;

      this.$refs.datePicker?.setTimeFromPreset(preset || METRICS_PERIODS.last_seven_days.value);
      this.liveAutoUpdate = this.getUserSetting("liveAutoUpdate") ?? true;
      !this.allSegmentsStore.segments.length &&
        this.allSegmentsStore.getSegments({ appId: this.currApp.id }, { nonBlocking: true });
    } else {
      this.liveAutoUpdate = false;
      await this.fetchMetrics();
    }
  },
  methods: {
    exportLiveDataToCsv() {
      this.loadingCsv = true;
      exportLiveDataToCsv(
        this.dataValues?.hits,
        this.tabs[this.tab].key,
        this.date.preset?.value,
        this.surveyEnabled,
        this.filterDataKeys,
      );
      this.loadingCsv = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.rf-live--header :deep(.rf-input.rf-input--base) {
  min-width: 10rem;
  max-width: 30rem;
}
</style>
